import React, { createContext, useContext, useState, ReactNode } from 'react';
import { FullScreenLoader } from '../components/ui/LoadingOverlay';

interface LoadingContextType {
  isLoading: boolean;
  loadingText: string;
  loadingVariant: 'default' | 'glass' | 'minimal' | 'pulse' | 'ai' | 'neural' | 'quantum';
  showLoading: (text?: string, variant?: 'default' | 'glass' | 'minimal' | 'pulse' | 'ai' | 'neural' | 'quantum') => void;
  hideLoading: () => void;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

interface LoadingProviderProps {
  children: ReactNode;
}

export const LoadingProvider: React.FC<LoadingProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('AI Processing...');
  const [loadingVariant, setLoadingVariant] = useState<'default' | 'glass' | 'minimal' | 'pulse' | 'ai' | 'neural' | 'quantum'>('ai');

  const showLoading = (text: string = 'AI Processing...', variant: 'default' | 'glass' | 'minimal' | 'pulse' | 'ai' | 'neural' | 'quantum' = 'ai') => {
    setLoadingText(text);
    setLoadingVariant(variant);
    setIsLoading(true);
  };

  const hideLoading = () => {
    setIsLoading(false);
  };

  return (
    <LoadingContext.Provider value={{
      isLoading,
      loadingText,
      loadingVariant,
      showLoading,
      hideLoading
    }}>
      {children}
      {isLoading && (
        <FullScreenLoader
          text={loadingText}
          variant={loadingVariant}
          size="2xl"
          showBackground={true}
        />
      )}
    </LoadingContext.Provider>
  );
};

export default LoadingContext;
